import { ReactElement, useMemo, useState } from 'react'
import { Typography, styled, ButtonBase, Radio } from '@mui/material'
import { AccessPointIcon, ChevronLeftIcon, TribuneIcon } from '@weblite/icons'
import { BottomDrawer } from '$common/bottom-drawer'
import { t } from '$i18n'
import { SecondaryButton } from '$common/buttons/secondary-button'
import {
  ButtonContent,
  ButtonContentHeader,
  CallTypeButton,
  CallTypeButtons,
  Root,
  StyledCheckCircleIcon,
} from '../shared/components'
import { useRoomCanMakeOnlineClass } from '$logic/hall/react-hook'

export type ChannelCallType = 'live' | 'tribune' | 'classroom'
interface CallItem {
  title: string
  description: string
  icon: ReactElement
  value: ChannelCallType
}
const items: CallItem[] = [
  {
    title: t('call.live'),
    description: t('chooseChannelCallModal.liveDescription'),
    icon: <AccessPointIcon />,
    value: 'live',
  },
  {
    title: t('call.tribune'),
    description: t('chooseChannelCallModal.tribuneDescription'),
    icon: <TribuneIcon />,
    value: 'tribune',
  },
]

export interface Props {
  isOpen?: boolean
  onConfirm: (callType: ChannelCallType) => unknown
  onCancel: () => unknown
  canMakeOnlineClass?: boolean
}
export function ChooseChannelCall({
  isOpen = false,
  onConfirm,
  onCancel,
  canMakeOnlineClass,
}: Props) {
  const [selectedCall, setSelectedCall] = useState<ChannelCallType>('live')

  const callItems: CallItem[] = useMemo(
    () =>
      canMakeOnlineClass
        ? [
            ...items,
            {
              title: t('call.classroom'),
              description: t('chooseChannelCallModal.classroomDescription'),
              icon: <AccessPointIcon />,
              value: 'classroom',
            },
          ]
        : items,
    [canMakeOnlineClass],
  )

  return (
    <BottomDrawer isOpen={isOpen} onClose={onCancel}>
      <Root>
        <Typography variant="subtitle1" color="secondary700">
          {t('chooseChannelCallModal.title')}
        </Typography>

        <CallTypeButtons>
          {callItems.map(({ title, description, icon, value }) => (
            <CallTypeButton
              data-call-type={value}
              data-selected={selectedCall === value}
              onClick={() => setSelectedCall(value)}
            >
              <ButtonContent>
                <ButtonContentHeader
                  data-call-type={value}
                  data-selected={selectedCall === value}
                >
                  {icon}
                  <Typography variant="subtitle1" color="inherit">
                    {title}
                  </Typography>
                </ButtonContentHeader>
                <Typography color="neutral900" variant="body2">
                  {description}
                </Typography>
              </ButtonContent>
              <Radio
                checked
                checkedIcon={
                  <StyledCheckCircleIcon
                    data-selected={selectedCall === value}
                  />
                }
              />
            </CallTypeButton>
          ))}
        </CallTypeButtons>

        <SecondaryButton
          fullWidth
          text={t('common.resume')}
          sx={{ height: '48px' }}
          onClick={() => onConfirm(selectedCall)}
          endIcon={<ChevronLeftIcon />}
        />
      </Root>
    </BottomDrawer>
  )
}
